import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { ContentSpacing, breakpoint, Breakpoints } from 'elc-base-theme';
import { Shade } from '../../domain/entities/Shade';
import {
    ShadeItemTheme,
    ColorFamilyRowTheme,
    StyledOkIconTheme,
    SelectedSwatchTheme
} from '../../../theme/default-theme';
import { OkIcon } from 'elc-icons';
import { SliderView, ISliderSettings } from 'elc-slider';
import { Caption, Heading6 } from 'elc-typography';
import { Desktop, Mobile } from 'elc-layouts';
import { IShadesByColorFamily } from '../../controllers/ProductShadesSidebarViewController';
import { ColorFamilyLabel } from './ColorFamilyLabel';
import { handleKeyPress, ACCEPTABLE_KEYS_PRESSED } from '../../utils/KeyPressHandler';
import { classes } from '../../utils/Classes';

export interface IProductShadesSidebarShadePicker {
    selectedShade?: Shade;
    onShadeSelect(name: string, productId: string): void;
    colorFamilies?: IShadesByColorFamily;
    shades: Shade[];
    productId: string;
}

interface IShadeItem {
    isShadeSelected?: boolean | undefined;
    color?: string;
}

const ShadeSwatch = styled.div`
    height: 56px;
    width: 56px;
    margin: 0 auto;
    background-color: ${({ color }: IShadeItem) => color};
    ${({ isShadeSelected }) => isShadeSelected && SelectedSwatchTheme};
    ${breakpoint('desktop')`
        height: 30px;
        width: 30px;
        margin: 0;
    `};
`;

const StyledLabel = styled(Caption)`
    margin: ${ContentSpacing.space6} auto;
    width: 80px;
`;

const ShadeItem = styled.div`
    text-align: center;
    user-select: none;
    display: flex;
    align-items: center;
    flex-direction: column;
    ${({ isShadeSelected, color }: IShadeItem) => isShadeSelected && `background-color:${color}`};
    &:hover {
        background-color: ${({ color }: IShadeItem) => color};
    }

    &:focus {
        outline: none;
    }

    ${breakpoint('desktop')`
        justify-content: space-between;
        text-align: left;
        flex-direction: initial;
        padding: ${ContentSpacing.space10};
        cursor: pointer;
        user-select: none;
        ${ShadeItemTheme}

        [dir='rtl'] & {
            text-align: right;
        }
    `};
`;

const ColorFamilyRow = styled(Heading6)`
    padding: ${ContentSpacing.space10} ${ContentSpacing.space10} ${ContentSpacing.space20};
    ${ColorFamilyRowTheme}
`;

const ColorFamilyGroup = styled.div`
    padding-bottom: ${ContentSpacing.space40};
`;

const StyledOkIcon = styled(OkIcon)`
    height: 30px;
    width: 30px;
    ${StyledOkIconTheme}
`;

const ShadeArray = styled.div`
    display: flex;
    margin-top: ${ContentSpacing.space20};

    .slick-slider {
        margin-bottom: 0;
    }
`;

const sliderSettings: ISliderSettings = {
    arrows: false,
    responsive: [
        {
            breakpoint: Breakpoints.desktop,
            settings: {
                slidesToShow: 5,
                slidesToScroll: 5
            }
        }
    ]
};

const acceptedKeys = [ACCEPTABLE_KEYS_PRESSED.ENTER, ACCEPTABLE_KEYS_PRESSED.SPACEBAR];

@observer
export class ProductShadesSidebarShadePicker extends React.Component<
    IProductShadesSidebarShadePicker
> {
    private selectShade = (e: React.SyntheticEvent<HTMLDivElement>) => {
        const shadeName = e.currentTarget.dataset.shadename || '';
        const { onShadeSelect, productId } = this.props;

        onShadeSelect(shadeName, productId);
    };

    private getShadePickerDesktop() {
        const { colorFamilies, selectedShade, onShadeSelect } = this.props;
        const selectedShadeItemClassNames = classes('shade-picker-item--selected');
        const shadeItemClassNames = classes('shade-picker-item');
        const shadeItemNameClassNames = classes('shade-picker-item-name');

        if (colorFamilies) {
            return (
                <Desktop minWidth={Breakpoints.desktop}>
                    {() =>
                        Object.keys(colorFamilies).map(family => (
                            <ColorFamilyGroup key={`color-family-${family}`}>
                                <ColorFamilyRow>
                                    <ColorFamilyLabel name={family} />
                                </ColorFamilyRow>
                                {colorFamilies[family].map((shade: Shade) => (
                                    <ShadeItem
                                        key={`${shade.hexVal}-shade`}
                                        color={shade.hexVal}
                                        isShadeSelected={
                                            selectedShade && selectedShade.name === shade.name
                                        }
                                        data-shadename={shade.name}
                                        onClick={this.selectShade}
                                        className={
                                            selectedShade && selectedShade.name === shade.name
                                                ? selectedShadeItemClassNames
                                                : shadeItemClassNames
                                        }
                                    >
                                        {selectedShade && selectedShade.name === shade.name ? (
                                            <StyledOkIcon />
                                        ) : (
                                            <ShadeSwatch color={shade.hexVal} />
                                        )}
                                        <Caption
                                            tabIndex={0}
                                            data-shadename={shade.name}
                                            onKeyPress={keyPressed => {
                                                handleKeyPress(
                                                    keyPressed,
                                                    acceptedKeys,
                                                    onShadeSelect,
                                                    shade.name
                                                );
                                            }}
                                            className={shadeItemNameClassNames}
                                        >
                                            {shade.name}
                                        </Caption>
                                    </ShadeItem>
                                ))}
                            </ColorFamilyGroup>
                        ))
                    }
                </Desktop>
            );
        }
    }

    private getShadePickerMobile() {
        return (
            <Mobile maxWidth={Breakpoints.desktop - 1}>
                {() => (
                    <ShadeArray>
                        <SliderView
                            sliderSettings={sliderSettings}
                            items={this.getSliderShades()}
                        />
                    </ShadeArray>
                )}
            </Mobile>
        );
    }

    private getSliderShades() {
        const { shades, onShadeSelect } = this.props;
        const shadeItemSwatchClassNames = classes('shade-picker-item-swatch');
        const shadeItemNameClassNames = classes('shade-picker-item-name');

        return shades.map(shade => (
            <ShadeItem
                key={`${shade.hexVal}-shade`}
                data-shadename={shade.name}
                onClick={this.selectShade}
                tabIndex={0}
                onKeyPress={keyPressed => {
                    handleKeyPress(keyPressed, acceptedKeys, onShadeSelect, shade.name);
                }}
            >
                <ShadeSwatch
                    className={shadeItemSwatchClassNames}
                    isShadeSelected={
                        this.props.selectedShade && this.props.selectedShade.name === shade.name
                    }
                    color={shade.hexVal}
                />
                <StyledLabel className={shadeItemNameClassNames}>{shade.name}</StyledLabel>
            </ShadeItem>
        ));
    }

    public render() {
        const { colorFamilies } = this.props;

        if (colorFamilies) {
            return (
                <>
                    {this.getShadePickerMobile()}
                    {this.getShadePickerDesktop()}
                </>
            );
        }

        return null;
    }
}

export default ProductShadesSidebarShadePicker;
